import React from 'react'
import { useTranslation } from 'react-i18next'
import type { DraggableProvided, DraggableStateSnapshot, DroppableProvided, DropResult } from '@hello-pangea/dnd'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'

import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import FormControlLabel from '@mui/material/FormControlLabel'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import LoadingButton from '@mui/lab/LoadingButton'
import PreviewIcon from '@mui/icons-material/Preview'
import CloseIcon from '@mui/icons-material/Close'

import InventoryLink from '../logs/inventoryLink'
import { get, post } from '../../utils/service'
import Alert from '../alert/alert'
import { getCuppingSampleLabel, createCuppingSessionSample } from '../../utils/cuppingUtils'
import { CuppingFormConfig } from '../../types/cuppingFormConfig'
import { PagedResponse } from '../../types/pagedResponse'
import { CuppingSessionStatus } from '../../types/enums/cuppingSessionStatus'
import { CuppingSession, CuppingSessionSample } from '../../types/cuppingSession'
import TooltipIconButton from '../fields/tooltipIconButton'
import CuppingScoreForm from './cuppingScoreForm'
import ExternalRoastDialog from './externalRoastDialog'
import CuppingSampleLink from './cuppingSampleLink'

interface CreateCuppingSessionFormProps {
  samples: Array<CuppingSessionSample>
  customerUrl: string
  sessionName: string
  cuppingFormUrl: string
  isBlind: boolean
  loadLogsErrors: Array<{ url: string; errorCode: number }>
  onCreateSuccess: (cuppingSessionId: string) => void
  onSamplesChanged: (samples: Array<string>) => void
}

const CreateCuppingSessionForm = ({ onSamplesChanged, ...props }: CreateCuppingSessionFormProps): JSX.Element => {
  const { t } = useTranslation()

  const [cuppingSessionName, setCuppingSessionName] = React.useState<string>(props.sessionName || '')
  const [selectedCuppingFormUrl, setSelectedCuppingFormUrl] = React.useState<string>(props.cuppingFormUrl || '')
  const [isBlind, setIsBlind] = React.useState<boolean>(props.isBlind || false)
  const [isLoadingCuppingForms, setIsLoadingCuppingForms] = React.useState<boolean>(true)
  const [cuppingForms, setCuppingForms] = React.useState<Array<CuppingFormConfig>>([])
  const [hasCuppingFormsLoadError, setHasCuppingFormsLoadError] = React.useState<boolean>(false)
  const [selectedRoasts, setSelectedRoasts] = React.useState<Array<CuppingSessionSample>>(props.samples)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [hasSaveError, setHasSaveError] = React.useState<boolean>(false)
  const [previewForm, setPreviewForm] = React.useState<CuppingFormConfig | null>(null)
  const [isExternalRoastDialogOpen, setIsExternalRoastDialogOpen] = React.useState<boolean>(false)

  React.useEffect(() => {
    setIsLoadingCuppingForms(true)
    get<PagedResponse<CuppingFormConfig>>('cuppingforms/')
      .then((response) => {
        setIsLoadingCuppingForms(false)
        setCuppingForms(response.data.results)
      })
      .catch(() => {
        setIsLoadingCuppingForms(false)
        setHasCuppingFormsLoadError(true)
      })
  }, [])

  React.useEffect(() => {
    onSamplesChanged(selectedRoasts.map((x) => x.log || x.external_log_name || ''))
  }, [selectedRoasts, onSamplesChanged])

  const postCuppingSession = (): void => {
    const cuppingSessionData = {
      cupping_form: selectedCuppingFormUrl,
      customer: props.customerUrl,
      status: CuppingSessionStatus.InProgress,
      name: cuppingSessionName.trim(),
      is_blind: isBlind,
      cupping_samples: selectedRoasts,
    }
    setIsSaving(true)
    setHasSaveError(false)
    post<Partial<CuppingSession>, CuppingSession>('cuppingsessions/', cuppingSessionData)
      .then((result) => {
        setIsSaving(false)
        props.onCreateSuccess(result.data.id)
      })
      .catch(() => {
        setIsSaving(false)
        setHasSaveError(true)
      })
  }

  const onDragEnd = (result: DropResult): void => {
    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return
    }
    // no movement
    if (result.destination.index === result.source.index) {
      return
    }
    const swappedSelectedRoasts = [...selectedRoasts]
    swappedSelectedRoasts.splice(result.destination.index, 0, swappedSelectedRoasts.splice(result.source.index, 1)[0])
    setSelectedRoasts(swappedSelectedRoasts)
  }

  return (
    <Stack>
      {isExternalRoastDialogOpen && (
        <ExternalRoastDialog
          onClose={(): void => setIsExternalRoastDialogOpen(false)}
          onAddExternalRoast={(externalRoastName: string) => {
            setIsExternalRoastDialogOpen(false)
            const externalCuppingSample = createCuppingSessionSample(null, externalRoastName)
            setSelectedRoasts((oldSelectedRoasts) => [...oldSelectedRoasts, externalCuppingSample])
          }}
        />
      )}
      {previewForm && (
        <Dialog open={true} fullScreen onClose={() => setPreviewForm(null)}>
          <DialogContent>
            <Stack maxWidth="sm" width="100%">
              <CuppingScoreForm
                sampleLabel={<></>}
                resultSample={{
                  cupping_session_sample: 1,
                  descriptors: [],
                  notes: '',
                  total_score: 0,
                  scores: previewForm.form_fields.map((x) => {
                    return {
                      cupping_form_field: x.id,
                      score: x.default_value || 0,
                    }
                  }),
                }}
                cuppingFormConfig={previewForm}
                totalScore={0}
                availableDescriptors={[]}
                onUpdateScore={() => {
                  /** */
                }}
                onUpdateDescriptors={() => {
                  /** */
                }}
                onUpdateNotes={() => {
                  /** */
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewForm(null)}>{t('cupping.closepreview')}</Button>
          </DialogActions>
        </Dialog>
      )}
      {hasCuppingFormsLoadError && <Alert severity="error">{t('cupping.loadcuppingformserror')}</Alert>}
      {isLoadingCuppingForms && <CircularProgress />}
      <DragDropContext onDragEnd={onDragEnd}>
        {!isLoadingCuppingForms && (
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                required
                label={t('cupping.cuppingsessionname')}
                value={cuppingSessionName}
                onChange={(event) => setCuppingSessionName(event.target.value)}
              />
              <Stack direction="row">
                <FormControl sx={{ flex: 1 }} required>
                  <InputLabel htmlFor="cupping-form-select">{t('cupping.cuppingform')}</InputLabel>
                  <Select
                    native
                    required
                    id="cupping-form-select"
                    value={selectedCuppingFormUrl}
                    label={t('cupping.cuppingform')}
                    size="small"
                    onChange={(event: SelectChangeEvent): void => setSelectedCuppingFormUrl(event.target.value)}
                  >
                    <option disabled value="">
                      {t('cupping.selectform')}
                    </option>
                    {cuppingForms.map((x) => (
                      <option key={x.url} value={x.url}>
                        {x.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <TooltipIconButton
                  onClick={() => setPreviewForm(cuppingForms.find((x) => x.url === selectedCuppingFormUrl) || null)}
                  icon={<PreviewIcon />}
                  tooltip={t('cupping.previewcuppingform')}
                  disabled={!selectedCuppingFormUrl}
                />
              </Stack>
              <FormControlLabel
                control={<Switch checked={isBlind} onChange={() => setIsBlind((oldIsBlind) => !oldIsBlind)} />}
                label={t('cupping.blindcupping')}
              />
            </Stack>
            {/** overflow hidden in element below to remove warning from dnd framework */}
            <TableContainer sx={{ overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 65 }}>{t('cupping.sample')}</TableCell>
                    <TableCell>{t('cupping.name')}</TableCell>
                    <TableCell>{t('cupping.beanname')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <Droppable droppableId="table">
                  {(droppableProvided: DroppableProvided) => (
                    <TableBody
                      ref={(ref: HTMLElement | null) => {
                        droppableProvided.innerRef(ref)
                      }}
                      {...droppableProvided.droppableProps}
                    >
                      {selectedRoasts.map((x, index) => {
                        const key = x.log_data?.url || x.external_log_name || ''
                        return (
                          <Draggable draggableId={key} index={index} key={key}>
                            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                              return (
                                <TableRow
                                  ref={provided?.innerRef}
                                  sx={snapshot.isDragging ? { display: 'table', cursor: 'grab' } : { cursor: 'grab' }}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <TableCell sx={{ width: 85 }}>{getCuppingSampleLabel(index)}</TableCell>
                                  <TableCell>
                                    <CuppingSampleLink sample={x} />
                                  </TableCell>
                                  <TableCell>
                                    <InventoryLink log={x.log_data} />
                                  </TableCell>
                                  <TableCell sx={{ width: 65 }}>
                                    <TooltipIconButton
                                      onClick={() => {
                                        setSelectedRoasts((oldSelectedRoasts) =>
                                          oldSelectedRoasts.filter((y) => {
                                            return (
                                              y.log_data?.id !== x.log_data?.id ||
                                              y.external_log_name !== x.external_log_name
                                            )
                                          })
                                        )
                                      }}
                                      icon={<CloseIcon />}
                                      tooltip={t('cupping.removesample')}
                                    />
                                  </TableCell>
                                </TableRow>
                              )
                            }}
                          </Draggable>
                        )
                      })}
                      {droppableProvided.placeholder}
                      {selectedRoasts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10}>
                            <Alert severity="info">{t('cupping.noselectedsamples')}</Alert>
                          </TableCell>
                        </TableRow>
                      )}
                      {props.loadLogsErrors && props.loadLogsErrors.some((x) => x.errorCode === 404) && (
                        <TableRow>
                          <TableCell colSpan={10}>
                            <Alert severity="warning">
                              {t('cupping.logsdeletedwarning').replace(
                                '{0}',
                                `${props.loadLogsErrors.filter((x) => x.errorCode === 404).length}`
                              )}
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                      {props.loadLogsErrors &&
                        props.loadLogsErrors.some((x) => x.errorCode > 0 && x.errorCode !== 404) && (
                          <TableRow>
                            <TableCell colSpan={10}>
                              <Alert severity="error">{t('cupping.loadlogserror')}</Alert>
                            </TableCell>
                          </TableRow>
                        )}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </TableContainer>
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={() => setIsExternalRoastDialogOpen(true)}>
                {t('cupping.addexternalsample')}
              </Button>
              <LoadingButton
                variant="contained"
                loading={isSaving}
                disabled={!cuppingSessionName.trim() || !selectedCuppingFormUrl || selectedRoasts.length === 0}
                onClick={postCuppingSession}
              >
                {t('cupping.startcuppingsession')}
              </LoadingButton>
            </Stack>
            {hasSaveError && <Alert severity="error">{t('cupping.startcuppingsessionerror')}</Alert>}
          </Stack>
        )}
      </DragDropContext>
    </Stack>
  )
}

export default CreateCuppingSessionForm
